<script setup lang="ts">
import UpdateCompanyFormDialog from '@/features/companies/dialogs/UpdateCompanyFormDialog.vue'
import { useDynamicDialog } from '@/shared/composables/UseDynamicDialog'
import { usePermissions } from '@/shared/composables/UsePermissions'
import {
  getAdminGetSalesForecastQueryKey,
  getAdminGetSubscriptionMetricsQueryKey,
  useAdminGetSubscriptionMetrics,
} from '@swipe4work/api-orval'
import type {
  AdminGetSubscriptionMetricsParams,
  GetSubscriptionMetricsSubscriptionRowDto,
} from '@swipe4work/models-orval'
import { AdminPermissionResource, PermissionLevel } from '@swipe4work/models-orval'
import { useQueryClient } from '@tanstack/vue-query'

definePage({
  path: '/sales/sales-metrics/subscriptions',
  name: '/sales/SalesMetrics/subscriptions',
  meta: {
    permission: {
      resource: 'SalesMetrics',
      level: 'Read',
    },
  },
})

type SubscriptionFilterState = {
  onlyOverridden: boolean
}

const dialog = useDynamicDialog()
const queryClient = useQueryClient()
const { can } = usePermissions()

const {
  filters,
  searchInput,
  search,
  sortField,
  sortDescending,
  offset,
  rows,
  ROWS_PER_PAGE_OPTIONS,
  onSort,
  onPage,
} = useDataTableFilters<SubscriptionFilterState>({
  schema: { onlyOverridden: 'boolean' },
  defaultFilters: () => ({ onlyOverridden: false }),
  defaultPageSize: 20,
  defaultSortField: 'arrCents',
  defaultSortDescending: true,
})

// The toggle sits inline instead of in a filter panel, so it has to rewind the paging itself.
watch(() => filters.onlyOverridden, () => {
  offset.value = 0
})

const params = computed<AdminGetSubscriptionMetricsParams>(() => ({
  search: search.value,
  onlyOverridden: filters.onlyOverridden || undefined,
  sortField: sortField.value,
  sortDescending: sortDescending.value,
  limit: rows.value,
  offset: offset.value,
}))

const { data, isPending, isError } = useAdminGetSubscriptionMetrics(params)

const result = computed(() => data.value)
const subscriptions = computed<GetSubscriptionMetricsSubscriptionRowDto[]>(() => result.value?.rows ?? [])
const totalCount = computed(() => result.value?.totalCount ?? 0)

const eurFormatter = new Intl.NumberFormat('nl-NL', {
  style: 'currency',
  currency: 'EUR',
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
})

const formatCents = (cents: number | null | undefined) =>
  cents == null ? '—' : eurFormatter.format(cents / 100)

const formatDate = (date: Date | string | null | undefined) => {
  if (!date) return '—'

  return new Intl.DateTimeFormat('nl-NL', { dateStyle: 'short' }).format(new Date(date))
}

const canEditCompany = computed(() => can(AdminPermissionResource.Company, PermissionLevel.Write))

function openCompanyDialog(row: GetSubscriptionMetricsSubscriptionRowDto) {
  dialog.open(UpdateCompanyFormDialog, {
    props: {
      modal: true,
      closable: true,
      showHeader: false,
      dismissableMask: true,
      style: {
        width: 'auto',
        height: 'auto',
      },
    },
    data: { companyId: row.companyId },
    emits: {
      // An edited ARR override moves both this table and the forecast's current-ARR tile.
      onUpdateCompany: async () => {
        await Promise.all([ queryClient.invalidateQueries({ queryKey: getAdminGetSubscriptionMetricsQueryKey() }), queryClient.invalidateQueries({ queryKey: getAdminGetSalesForecastQueryKey() }) ])
      },
    },
  })
}
</script>

<template>
  <div class="space-y-4">
    <div v-if="isPending" class="grid grid-cols-2 gap-4 lg:grid-cols-5 sm:grid-cols-3">
      <Skeleton v-for="n in 5" :key="n" height="5rem" />
    </div>

    <Message v-else-if="isError" severity="error">
      Er is iets misgegaan bij het laden van de abonnementen.
    </Message>

    <template v-else>
      <div class="grid grid-cols-2 gap-4 lg:grid-cols-5 sm:grid-cols-3">
        <StatTile
          label="Bedrijven met abonnement"
          :value="String(result?.totalCompanies ?? 0)"
          subtitle="Lopende abonnementen en overrides"
        />
        <StatTile
          label="MRR"
          :value="formatCents(result?.totalMrrCents)"
          subtitle="Maandelijkse recurring revenue"
        />
        <StatTile
          label="ARR"
          :value="formatCents(result?.totalArrCents)"
          subtitle="Jaarlijkse recurring revenue"
        />
        <StatTile
          label="Gem. MRR per bedrijf"
          :value="formatCents(result?.averageMrrCents)"
        />
        <StatTile
          label="Overschreven ARR"
          :value="String(result?.totalOverriddenCompanies ?? 0)"
          :subtitle="`${result?.totalOverriddenJobSlotCompanies ?? 0} met overschreven vacature slots`"
        />
      </div>

      <Card>
        <template #content>
          <div class="flex flex-col gap-4">
            <div class="flex flex-wrap items-center justify-between gap-3">
              <h3 class="text-surface-900 font-bold">
                Abonnementen
              </h3>
              <div class="flex flex-wrap items-center gap-3">
                <div class="flex items-center gap-2">
                  <ToggleSwitch v-model="filters.onlyOverridden" input-id="onlyOverridden" />
                  <label for="onlyOverridden" class="text-sm">
                    Alleen overschreven ARR
                    <span class="text-surface-400">({{ result?.totalOverriddenCompanies ?? 0 }})</span>
                  </label>
                </div>

                <IconField class="max-w-xs flex-1">
                  <InputIcon class="fa-regular fa-search" />
                  <InputText v-model="searchInput" placeholder="Zoeken..." class="w-full" />
                  <InputIcon
                    v-if="searchInput"
                    class="fa-regular fa-xmark cursor-pointer"
                    style="pointer-events: auto"
                    @click="searchInput = ''"
                  />
                </IconField>
              </div>
            </div>

            <DataTable
              :value="subscriptions"
              :lazy="true"
              :first="offset"
              :rows="rows"
              :rows-per-page-options="ROWS_PER_PAGE_OPTIONS"
              :total-records="totalCount"
              :sort-field="sortField"
              :sort-order="sortDescending ? -1 : 1"
              paginator
              paginator-template="CurrentPageReport FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink"
              current-page-report-template="{first}-{last} van {totalRecords}"
              size="small"
              class="w-full"
              row-hover
              @page="onPage"
              @sort="onSort"
            >
              <template #empty>
                <span class="text-sm text-surface-400">
                  {{ filters.onlyOverridden
                    ? 'Geen bedrijven met een overschreven ARR.'
                    : 'Geen lopende abonnementen gevonden.' }}
                </span>
              </template>
              <Column field="companyName" header="Bedrijf" sortable>
                <template #body="{ data: row }">
                  <span class="text-sm font-medium">{{ row.companyName }}</span>
                </template>
              </Column>
              <Column field="subscriptionTitle" header="Abonnement" sortable style="min-width: 10rem">
                <template #body="{ data: row }">
                  <span v-if="row.subscriptionTitle" class="text-sm">{{ row.subscriptionTitle }}</span>
                  <span v-else class="text-sm text-surface-400 italic">Geen abonnement</span>
                </template>
              </Column>
              <Column header="Vacature slots" style="width: 9rem">
                <template #body="{ data: row }">
                  <div v-if="row.assignedJobSlots != null" class="flex flex-col items-start gap-1">
                    <span class="text-sm font-mono">{{ row.assignedJobSlots }}</span>
                    <Tag value="Overschreven" severity="warn" />
                  </div>
                  <span v-else class="text-sm font-mono" :class="row.subscriptionJobSlots == null ? 'text-surface-400' : ''">
                    {{ row.subscriptionJobSlots ?? '—' }}
                  </span>
                </template>
              </Column>
              <Column field="mrrCents" header="MRR" sortable style="width: 8rem">
                <template #body="{ data: row }">
                  <span class="text-sm font-mono">{{ formatCents(row.mrrCents) }}</span>
                </template>
              </Column>
              <Column field="arrCents" header="ARR" sortable style="width: 10rem">
                <template #body="{ data: row }">
                  <div class="flex flex-col items-start gap-1">
                    <span class="text-sm font-mono">{{ formatCents(row.arrCents) }}</span>
                    <template v-if="row.arrOverrideCents != null">
                      <Tag value="Overschreven" severity="warn" />
                      <span class="text-xs text-surface-400">
                        Abonnement: {{ formatCents(row.subscriptionArrCents) }}
                      </span>
                    </template>
                  </div>
                </template>
              </Column>
              <Column field="startDate" header="Begindatum" sortable style="width: 8rem">
                <template #body="{ data: row }">
                  {{ formatDate(row.startDate) }}
                </template>
              </Column>
              <Column field="endDate" header="Einddatum" sortable style="width: 8rem">
                <template #body="{ data: row }">
                  <span :class="row.endDate ? '' : 'text-surface-400'">
                    {{ row.endDate ? formatDate(row.endDate) : 'Doorlopend' }}
                  </span>
                </template>
              </Column>
              <Column style="width: 4rem">
                <template #body="{ data: row }">
                  <Button
                    v-if="canEditCompany"
                    icon="fa-regular fa-edit"
                    text
                    rounded
                    severity="secondary"
                    v-tooltip.left="'Wijzig bedrijf'"
                    @click="openCompanyDialog(row)"
                  />
                </template>
              </Column>
            </DataTable>
          </div>
        </template>
      </Card>
    </template>
  </div>
</template>
